import { useState, useEffect } from "react";
import { Building2, Plus, Pencil, Trash2, Globe, Phone, Mail, Facebook, Instagram, Twitter, ImagePlus, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { getTeams, getSponsors, createSponsor, updateSponsor, deleteSponsor } from "../lib/api";
import { toast } from "sonner";

export default function Sponsors() {
  const [teams, setTeams] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSponsor, setSelectedSponsor] = useState(null);
  const [formData, setFormData] = useState({
    team_id: "",
    business_name: "",
    description: "",
    contact_phone: "",
    contact_email: "",
    website: "",
    facebook: "",
    instagram: "",
    twitter: "",
    images: [],
  });
  const [newImageUrl, setNewImageUrl] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [teamsRes, sponsorsRes] = await Promise.all([
        getTeams(),
        getSponsors()
      ]);
      setTeams(teamsRes.data);
      setSponsors(sponsorsRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (sponsor = null) => {
    if (sponsor) {
      setSelectedSponsor(sponsor);
      setFormData({
        team_id: sponsor.team_id,
        business_name: sponsor.business_name,
        description: sponsor.description,
        contact_phone: sponsor.contact_phone || "",
        contact_email: sponsor.contact_email || "",
        website: sponsor.website || "",
        facebook: sponsor.facebook || "",
        instagram: sponsor.instagram || "",
        twitter: sponsor.twitter || "",
        images: sponsor.images || [],
      });
    } else {
      setSelectedSponsor(null);
      setFormData({
        team_id: "",
        business_name: "",
        description: "",
        contact_phone: "",
        contact_email: "",
        website: "",
        facebook: "",
        instagram: "",
        twitter: "",
        images: [],
      });
    }
    setNewImageUrl("");
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedSponsor(null);
  };

  const handleAddImage = () => {
    if (newImageUrl.trim()) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, newImageUrl.trim()]
      }));
      setNewImageUrl("");
    }
  };

  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.team_id || !formData.business_name || !formData.description) {
      toast.error("Please fill in required fields");
      return;
    }

    setSaving(true);
    try {
      if (selectedSponsor) {
        await updateSponsor(selectedSponsor.id, formData);
        toast.success("Sponsor updated successfully");
      } else {
        await createSponsor(formData);
        toast.success("Sponsor created successfully");
      }
      handleCloseDialog();
      fetchData();
    } catch (error) {
      toast.error("Failed to save sponsor");
    } finally {
      setSaving(false);
    }
  };

  const openDeleteDialog = (sponsor) => {
    setSelectedSponsor(sponsor);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedSponsor) return;
    try {
      await deleteSponsor(selectedSponsor.id);
      toast.success("Sponsor deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedSponsor(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete sponsor");
    }
  };

  const getTeamName = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    return team?.name || "Unknown";
  };

  const getTeamLogo = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    return team?.logo;
  };

  // Get teams without sponsors
  const teamsWithoutSponsors = teams.filter(
    team => !sponsors.some(s => s.team_id === team.id)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tight">
            Sponsors
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage team sponsors and their business information
          </p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider btn-glow"
          disabled={teamsWithoutSponsors.length === 0}
          data-testid="add-sponsor-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Sponsor
        </Button>
      </div>

      {/* Sponsors Grid */}
      {sponsors.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sponsors.map((sponsor, index) => (
            <div
              key={sponsor.id}
              className="bg-card border border-border rounded-xl overflow-hidden animate-fade-in opacity-0"
              style={{ animationDelay: `${index * 50}ms` }}
              data-testid={`sponsor-card-${sponsor.id}`}
            >
              {/* Team Header */}
              <div className="p-4 bg-secondary/50 flex items-center gap-3">
                {getTeamLogo(sponsor.team_id) ? (
                  <img src={getTeamLogo(sponsor.team_id)} alt="" className="w-12 h-12 rounded-lg object-cover" />
                ) : (
                  <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-muted-foreground" />
                  </div>
                )}
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Sponsor of</p>
                  <p className="font-bold">{getTeamName(sponsor.team_id)}</p>
                </div>
              </div>

              {/* Sponsor Info */}
              <div className="p-4 space-y-3">
                <h3 className="font-heading text-lg font-bold uppercase">{sponsor.business_name}</h3>
                <p className="text-sm text-muted-foreground line-clamp-3">{sponsor.description}</p>

                {/* Contact Info */}
                <div className="space-y-1 text-sm">
                  {sponsor.contact_phone && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Phone className="w-3 h-3" />
                      <span>{sponsor.contact_phone}</span>
                    </div>
                  )}
                  {sponsor.contact_email && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Mail className="w-3 h-3" />
                      <span>{sponsor.contact_email}</span>
                    </div>
                  )}
                  {sponsor.website && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Globe className="w-3 h-3" />
                      <a href={sponsor.website} target="_blank" rel="noopener noreferrer" className="hover:text-primary truncate">
                        {sponsor.website}
                      </a>
                    </div>
                  )}
                </div>

                {/* Social Links */}
                {(sponsor.facebook || sponsor.instagram || sponsor.twitter) && (
                  <div className="flex gap-2 pt-2">
                    {sponsor.facebook && (
                      <a href={sponsor.facebook} target="_blank" rel="noopener noreferrer" className="p-2 bg-secondary rounded-lg hover:bg-secondary/80">
                        <Facebook className="w-4 h-4" />
                      </a>
                    )}
                    {sponsor.instagram && (
                      <a href={sponsor.instagram} target="_blank" rel="noopener noreferrer" className="p-2 bg-secondary rounded-lg hover:bg-secondary/80">
                        <Instagram className="w-4 h-4" />
                      </a>
                    )}
                    {sponsor.twitter && (
                      <a href={sponsor.twitter} target="_blank" rel="noopener noreferrer" className="p-2 bg-secondary rounded-lg hover:bg-secondary/80">
                        <Twitter className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                )}

                {/* Images Preview */}
                {sponsor.images?.length > 0 && (
                  <div className="flex gap-2 pt-2 overflow-x-auto">
                    {sponsor.images.slice(0, 3).map((img, i) => (
                      <img key={i} src={img} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0" />
                    ))}
                    {sponsor.images.length > 3 && (
                      <div className="w-16 h-16 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                        <span className="text-xs text-muted-foreground">+{sponsor.images.length - 3}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-3 border-t border-border">
                  <Button variant="secondary" size="sm" onClick={() => handleOpenDialog(sponsor)} className="flex-1">
                    <Pencil className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => openDeleteDialog(sponsor)} className="text-destructive hover:text-destructive hover:bg-destructive/10">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <Building2 className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="font-heading text-xl font-bold uppercase mb-2">No Sponsors Yet</h3>
          <p className="text-muted-foreground mb-6">Add sponsor information for your team sponsors</p>
          <Button onClick={() => handleOpenDialog()} className="bg-primary text-primary-foreground">
            <Plus className="w-4 h-4 mr-2" />
            Add First Sponsor
          </Button>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase">
              {selectedSponsor ? "Edit Sponsor" : "Add Sponsor"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Team Selection */}
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Team *
              </Label>
              <Select
                value={formData.team_id || "none"}
                onValueChange={(v) => setFormData(prev => ({ ...prev, team_id: v === "none" ? "" : v }))}
                disabled={!!selectedSponsor}
              >
                <SelectTrigger className="bg-background border-border">
                  <SelectValue placeholder="Select team" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="none">Select team</SelectItem>
                  {(selectedSponsor ? teams : teamsWithoutSponsors).map(team => (
                    <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Business Name */}
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Business Name *
              </Label>
              <Input
                value={formData.business_name}
                onChange={(e) => setFormData(prev => ({ ...prev, business_name: e.target.value }))}
                placeholder="Enter business name"
                className="bg-background border-border"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Description *
              </Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Write about the business..."
                rows={4}
                className="bg-background border-border"
              />
            </div>

            {/* Contact Info */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Phone
                </Label>
                <Input
                  value={formData.contact_phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_phone: e.target.value }))}
                  placeholder="+1 234 567 8900"
                  className="bg-background border-border"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Email
                </Label>
                <Input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
                  placeholder="contact@business.com"
                  className="bg-background border-border"
                />
              </div>
            </div>

            {/* Website */}
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Website
              </Label>
              <Input
                value={formData.website}
                onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                placeholder="https://www.business.com"
                className="bg-background border-border"
              />
            </div>

            {/* Social Media */}
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Facebook
                </Label>
                <Input
                  value={formData.facebook}
                  onChange={(e) => setFormData(prev => ({ ...prev, facebook: e.target.value }))}
                  placeholder="Facebook URL"
                  className="bg-background border-border"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Instagram
                </Label>
                <Input
                  value={formData.instagram}
                  onChange={(e) => setFormData(prev => ({ ...prev, instagram: e.target.value }))}
                  placeholder="Instagram URL"
                  className="bg-background border-border"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Twitter
                </Label>
                <Input
                  value={formData.twitter}
                  onChange={(e) => setFormData(prev => ({ ...prev, twitter: e.target.value }))}
                  placeholder="Twitter URL"
                  className="bg-background border-border"
                />
              </div>
            </div>

            {/* Images */}
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Images
              </Label>
              <div className="flex gap-2">
                <Input
                  value={newImageUrl}
                  onChange={(e) => setNewImageUrl(e.target.value)}
                  placeholder="Paste image URL"
                  className="bg-background border-border"
                />
                <Button type="button" variant="secondary" onClick={handleAddImage}>
                  <ImagePlus className="w-4 h-4" />
                </Button>
              </div>
              {formData.images.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.images.map((img, i) => (
                    <div key={i} className="relative group">
                      <img src={img} alt="" className="w-20 h-20 rounded-lg object-cover" />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(i)}
                        className="absolute -top-2 -right-2 p-1 bg-destructive text-destructive-foreground rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={handleCloseDialog} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="flex-1 bg-primary text-primary-foreground">
                {saving ? "Saving..." : selectedSponsor ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Sponsor</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this sponsor? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
